/**
 * Whether a browser runs WebKit, Safari's engine: Safari on macOS and every
 * browser on iOS and iPadOS, where Apple requires WebKit. The same test as
 * the Firestore transport choice in webapp `firebaseApp.ts`: Chromium is the
 * only other engine that claims AppleWebKit, iOS Chrome reports CriOS rather
 * than Chrome, and `GestureEvent` exists only in WebKit.
 *
 * @param args.userAgent - The browser's user agent.
 * @param args.hasGestureEvent - Whether the browser defines `GestureEvent`.
 * @return True on WebKit.
 * @throws Never.
 */
export function isWebKitEngine({
  userAgent,
  hasGestureEvent,
}: {
  userAgent: string;
  hasGestureEvent: boolean;
}): boolean {
  return (
    (/AppleWebKit/.test(userAgent) && !/Chrome/.test(userAgent)) ||
    /\b(iPad|iPhone|iPod)\b/.test(userAgent) ||
    hasGestureEvent
  );
}

/**
 * Whether this page runs in WebKit, decided once when the module loads; see
 * {@link isWebKitEngine}. False outside a browser.
 */
export const IS_WEBKIT =
  typeof navigator !== 'undefined' &&
  isWebKitEngine({
    userAgent: navigator.userAgent,
    hasGestureEvent: typeof window !== 'undefined' && 'GestureEvent' in window,
  });
